import React from 'react';
import { formatCurrency } from 'utils';
import { Stack, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { QuoteDepositType } from '@trnsact/trnsact-shared-types';
import { CurrencyInputField, InputField, InterestInputField, SelectField } from 'components/form/rhf';

import { DealBuilderFormValues } from '../../../types';
import { useDealBuilderContext } from '../../../context';
import { CUSTOMER_DEPOSIT_TYPE_OPTIONS } from '../../../constants';

export const CustomerDepositForm = () => {
  const { control } = useFormContext<DealBuilderFormValues>();

  const {
    pricing: { priceCalc },
  } = useDealBuilderContext();

  const depositType = useWatch({ control, name: 'pricing.customerDeposit.type' });
  const depositAmount = useWatch({ control, name: 'pricing.customerDeposit.value' });

  const { invoice, equipment, attachments } = priceCalc?.totals ?? {};

  const totalBeforeDeposit =
    (equipment?.price ?? 0) +
    (attachments?.price ?? 0) +
    (invoice?.fees ?? 0) +
    (invoice?.taxes ?? 0) +
    (equipment?.fees ?? 0) +
    (equipment?.taxes ?? 0) +
    (attachments?.fees ?? 0) +
    (attachments?.taxes ?? 0);

  const calculatedDepositAmount =
    depositType === QuoteDepositType.Percentage ? (depositAmount / 100) * totalBeforeDeposit : depositAmount;

  return (
    <Stack gap="1rem">
      <Typography variant="h6" component="span">
        Customer Deposit
      </Typography>

      <Stack direction="row" gap="1rem">
        <SelectField
          variant="outlined"
          control={control}
          label="Deposit Type"
          name="pricing.customerDeposit.type"
          options={CUSTOMER_DEPOSIT_TYPE_OPTIONS}
        />

        {depositType === QuoteDepositType.Flat ? (
          <CurrencyInputField
            control={control}
            label="Deposit Amount"
            name="pricing.customerDeposit.value"
            textFieldProps={{ variant: 'outlined' }}
          />
        ) : (
          <InterestInputField
            control={control}
            label="Deposit (%)"
            name="pricing.customerDeposit.value"
            textFieldProps={{ variant: 'outlined', size: 'small' }}
          />
        )}

        <InputField
          label="Notes"
          control={control}
          name="pricing.customerDeposit.notes"
          inputProps={{
            variant: 'outlined',
            placeholder: 'Enter note if deposit was paid with data received or is yet to be paid',
          }}
        />
      </Stack>

      <Stack direction="row">
        <Typography variant="body1">
          Deposit Amount:{' '}
          <Typography component="span" variant="body2" fontWeight={600}>
            {formatCurrency(calculatedDepositAmount, true)}
          </Typography>
        </Typography>
      </Stack>
    </Stack>
  );
};
